/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — canvas-wide context. Replaces the old
   `window.__orgChartSnap*` bridges (People Rebuild §3 — "snap
   targets flow through React context, never window globals"). A
   mutable ref carries the live snap-target list so segment-drag can
   read it every pointermove without a context re-render per frame;
   everything else here is plain reactive context state.
   ═══════════════════════════════════════════════════════════════ */

import React, { createContext, useContext } from 'react';
import type { NodeStylePrefs } from '../types';

export interface SnapTargets {
  xs: number[];
  ys: number[];
}

export interface OrgChartCanvasContextValue {
  canEdit: boolean;
  snapEnabled: boolean;
  /** Updated whenever nodes/waypoints settle — read (not subscribed)
   *  by edge segment-drag for corner snapping. */
  snapTargetsRef: React.MutableRefObject<SnapTargets>;
  nodeStyle: NodeStylePrefs;
}

const OrgChartCanvasContext = createContext<OrgChartCanvasContextValue | null>(null);

export function OrgChartCanvasProvider({
  value,
  children,
}: {
  value: OrgChartCanvasContextValue;
  children: React.ReactNode;
}) {
  return <OrgChartCanvasContext.Provider value={value}>{children}</OrgChartCanvasContext.Provider>;
}

export function useOrgChartCanvas(): OrgChartCanvasContextValue {
  const ctx = useContext(OrgChartCanvasContext);
  if (!ctx) {
    // Defensive default — every real render is wrapped by the
    // provider in OrgChartV2, but this keeps standalone unit
    // rendering (e.g. a future test) from throwing.
    throw new Error('useOrgChartCanvas must be used within <OrgChartCanvasProvider>');
  }
  return ctx;
}
